/* eslint-disable react/no-unknown-property */
import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";

const focusableSelector = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])'
].join(",");

const ClientModal = ({
  children,
  closeTestId,
  description,
  eyebrow,
  footer,
  modifier,
  onClose,
  title,
  titleTestId
}) => {
  const dialogRef = useRef(null);
  const onCloseRef = useRef(onClose);
  const titleId = `ccw-client-modal-${modifier}-title`;
  const descriptionId = `ccw-client-modal-${modifier}-description`;

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    dialogRef.current?.querySelector(".ccw-client-modal__close")?.focus();

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onCloseRef.current();
        return;
      }

      if (event.key !== "Tab" || !dialogRef.current) return;

      const focusableElements = Array.from(
        dialogRef.current.querySelectorAll(focusableSelector)
      ).filter((element) => element.getAttribute("aria-hidden") !== "true");

      if (focusableElements.length === 0) {
        event.preventDefault();
        dialogRef.current.focus();
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (
        event.shiftKey &&
        (document.activeElement === firstElement || document.activeElement === dialogRef.current)
      ) {
        event.preventDefault();
        lastElement.focus();
      } else if (
        !event.shiftKey &&
        (document.activeElement === lastElement || document.activeElement === dialogRef.current)
      ) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  return (
    <div className="clients-page-import-export-modal-container ccw-client-modal-backdrop">
      <section
        aria-describedby={description ? descriptionId : undefined}
        aria-labelledby={titleId}
        aria-modal="true"
        className={`clients-page-import-export-modal ccw-client-modal ccw-client-modal--${modifier}`}
        ref={dialogRef}
        role="dialog"
        tabIndex="-1"
      >
        <header className="ccw-client-modal__header">
          <div className="ccw-client-modal__heading">
            {eyebrow && <p className="ccw-client-modal__eyebrow">{eyebrow}</p>}
            <h1
              className="title modal-title ccw-client-modal__title"
              explorator_test_id={titleTestId}
              id={titleId}
            >
              {title}
            </h1>
            {description && (
              <p className="ccw-client-modal__description" id={descriptionId}>
                {description}
              </p>
            )}
          </div>
          <button
            aria-label={`Close ${title}`}
            className="modal-close-icon ccw-client-modal__close"
            explorator_test_id={closeTestId}
            onClick={onClose}
            type="button"
          >
            <i aria-hidden="true" className="fas fa-times" />
          </button>
        </header>

        <div className="ccw-client-modal__body">{children}</div>

        {footer && <footer className="modal-footer ccw-client-modal__footer">{footer}</footer>}
      </section>
    </div>
  );
};

ClientModal.propTypes = {
  children: PropTypes.node.isRequired,
  closeTestId: PropTypes.string,
  description: PropTypes.string,
  eyebrow: PropTypes.string,
  footer: PropTypes.node,
  modifier: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  titleTestId: PropTypes.string
};

export default ClientModal;
